import { zodResolver } from '@hookform/resolvers/zod';
import {
  HIGH_LEVEL_LABELS_TO_VALUES,
  METRIC_LABELS_TO_VALUES,
} from 'constants/metricLabelsToValues';
import { METRIC_VALUES_TO_LABELS } from 'constants/metricValuesToLabels';
import {
  selectCompanyName,
  selectFirstName,
  selectIsSegmentCardinalityImprovementsFeatureFlagOn,
  selectLastName,
  selectOrganizationId,
  selectUserProjectId,
  selectUserProjectName,
} from 'core/userContext';
import { selectRouterLocation } from 'modules/app/selectors';
import {
  useCampaignAndMetricFiltersContext,
  useTabContext,
  useTabSpecificFiltersContext,
  useTimeFiltersContext,
} from 'modules/messagingInsights/context';
import { PDFReportFormSchema, PDFReportFormStateValues } from 'modules/messagingInsights/schema';
import {
  ChartDisplay,
  RestDataIdentifier,
  SegmentTimeFrequency,
} from 'modules/messagingInsights/types';
import { useSegmentSortByParam } from 'modules/productAnalytics/hooks';
import { PDFReportType } from 'modules/productAnalytics/types';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { guessCurrentTimeZone } from 'utils/datetime';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { getParam } from 'utils/urlParams';
import { fetchPDFReportRequest } from '../actions';
import { useRestQueryParams } from '../hooks/useRestQueryParams';
import { selectSegmentData } from '../selectors';

export const PDFReportFormProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useDispatch();
  const isAnalyticsBotFiltering = useFeatureFlag('AnalyticsBotFiltering');
  const { showDeviceTab, showLocationTab } = useTabContext();
  const { campaignsSortedBy, campaignsSortDirection, audienceSortedBy, audienceSortDirection } =
    useTabSpecificFiltersContext();
  const { selectedCrossMetrics: metricSelectionOrder, mediumMetrics } =
    useCampaignAndMetricFiltersContext();
  const overviewQueryParams = useRestQueryParams(RestDataIdentifier.OVERVIEW);
  const campaignQueryParams = useRestQueryParams(RestDataIdentifier.CAMPAIGNS_FOR_METRICS);
  const { timezone } = useTimeFiltersContext();
  const isSegCardinalityFFOn = useSelector(selectIsSegmentCardinalityImprovementsFeatureFlagOn);
  const segmentData = useSelector(selectSegmentData);
  const projId = useSelector(selectUserProjectId) as number;
  const projName = useSelector(selectUserProjectName);
  const orgId = useSelector(selectOrganizationId) as number;
  const orgName = useSelector(selectCompanyName);
  const firstName = useSelector(selectFirstName);
  const lastName = useSelector(selectLastName);
  const { pathname = '', search = '' } = useSelector(selectRouterLocation);
  const env = getParam(`${pathname}${search}`, 'env') || '';

  const unsupportedMetrics = segmentData?.UnsupportedMetrics || [];
  const supportedMetrics = metricSelectionOrder.filter(
    (metric) => !unsupportedMetrics.includes(METRIC_VALUES_TO_LABELS[metric]),
  );
  const audienceSortByParam = useSegmentSortByParam(audienceSortedBy, supportedMetrics[0]);
  const METRIC_LABELS = METRIC_LABELS_TO_VALUES(isAnalyticsBotFiltering);

  const onSubmit = (formData: PDFReportFormStateValues) => {
    const queryParams = {
      ...overviewQueryParams,
      ...campaignQueryParams,
      timezone,
      currentTimezone: guessCurrentTimeZone(),
      mediumMetrics,
      campaignsSortedBy:
        HIGH_LEVEL_LABELS_TO_VALUES[
          campaignsSortedBy as keyof typeof HIGH_LEVEL_LABELS_TO_VALUES
        ] ||
        METRIC_LABELS[campaignsSortedBy as keyof typeof METRIC_LABELS] ||
        campaignsSortedBy,
      campaignsSortDirection,
      audienceSortDirection,
      audienceSortedBy: isSegCardinalityFFOn ? audienceSortByParam : {},
      showUnsupportedMetrics: true,
    };
    dispatch(fetchPDFReportRequest({ ...formData, queryParams }));
  };

  const defaultValues = {
    type: PDFReportType.MESSAGING_INSIGHTS,
    env: env || process.env.SERVICE,
    reportDetails: {
      name: `${projName} Report`,
      username: `${firstName} ${lastName}`,
    },
    organizationDetails: {
      orgName,
      projName,
      orgId,
      projId,
    },
    formSettings: {
      audienceSegment: '',
      locationRegion: '021',
      chartDisplay: ChartDisplay.ONE,
      segmentTimeFrequency: SegmentTimeFrequency.WEEKLY,
    },
    pages: {
      overview: true,
      campaigns: true,
      audience: true,
      timeSeries: true,
      locations: showLocationTab,
      devices: showDeviceTab,
    },
  };
  const methods = useForm<PDFReportFormStateValues>({
    resolver: zodResolver(PDFReportFormSchema),
    defaultValues,
    mode: 'all',
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>{children}</form>
    </FormProvider>
  );
};
